import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import { PROPHET_FAMILIES } from "@/data/prophetFamiliesData";
import { PROPHECIES } from "@/data/propheciesData";
import { ProphecyShareCard } from "./ProphecyShareCard";

/**
 * ProphecyWheel — bonus interactive section at the end of the Prophets
 * landing journey. Placed between Lore (`#lore`) and the Final CTA.
 *
 * Frontend-only default behavior. When used on the landing page (no
 * props), the wheel picks a random family + prophecy locally and
 * animates. When used inside the Hub, the parent passes optional props
 * (see forwardRef below) to make the reveal server-authoritative:
 *
 *   const ref = useRef(null);
 *   <ProphecyWheel
 *     ref={ref}
 *     hideBuiltInCTA
 *     hideBuiltInResult
 *     coreClickable={false}
 *     onRevealed={(famIdx, propIdx) => ...}
 *   />
 *   ref.current.triggerReveal({ famIdx, propIdx });
 *
 * The landing-page mount MUST remain functionally identical (unlimited
 * spins, no cooldown, no reward, no history, no Discord requirement).
 */

const FAMILY_COUNT = 10;
const SLOT_ANGLE = 360 / FAMILY_COUNT;         // 36° per slot
const MIN_SPIN_TURNS = 5;                       // full turns before landing
const MAX_SPIN_TURNS = 7;
const SPIN_MS = 3200;                           // deceleration duration
const REVEAL_MS = 900;                          // sci-fi reveal effect

// Random int in [min, max]
function rand(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

// Trigger a browser download for the given Blob under `filename`.
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduce(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

export const ProphecyWheel = forwardRef(function ProphecyWheel(props, ref) {
  const {
    hideBuiltInCTA = false,
    hideBuiltInResult = false,
    coreClickable = true,
    onRevealed = null,
  } = props || {};
  const sectionRef = useRef(null);
  const reduceMotion = useReducedMotion();

  // Mobile viewport tracking — when true, the .prophecy-result card is
  // portalled to <div id="prophecy-mobile-reveal-slot"> which lives
  // OUTSIDE the shared Insight frame, so the base frame stays compact.
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSlot, setMobileSlot] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => {
      setIsMobile(mq.matches);
      setMobileSlot(document.getElementById("prophecy-mobile-reveal-slot"));
    };
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  // Positions used to place each symbol around the ring. Precomputed once.
  const slots = useMemo(
    () =>
      PROPHET_FAMILIES.map((fam, i) => ({
        fam,
        angle: i * SLOT_ANGLE,
      })),
    [],
  );

  // Interaction state.
  const [rotation, setRotation] = useState(0);         // cumulative wheel rotation (deg)
  const [status, setStatus] = useState("idle");        // idle | spinning | revealing | revealed
  const [pickedIdx, setPickedIdx] = useState(null);    // family index in wheel
  const [prophecyIdx, setProphecyIdx] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareState, setShareState] = useState("idle"); // idle | generating
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState("");
  const previewBlobRef = useRef(null);            // Blob kept for direct download
  const shareBtnRef = useRef(null);                // used to restore focus on close
  const modalDownloadBtnRef = useRef(null);        // initial focus target inside modal
  const modalCloseBtnRef = useRef(null);
  const shareCardRef = useRef(null);
  const lastComboRef = useRef({ fam: -1, prop: -1 });
  const copyTimeoutRef = useRef(null);
  const shareTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const pickedFamily = pickedIdx == null ? null : slots[pickedIdx].fam;
  const pickedProphecy = prophecyIdx == null ? null : PROPHECIES[prophecyIdx];

  // Modal side-effects: escape-to-close, body-scroll lock, initial focus.
  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePreview();
      } else if (e.key === "Tab") {
        // Simple focus trap between the two buttons inside the modal.
        const focusables = [modalDownloadBtnRef.current, modalCloseBtnRef.current].filter(Boolean);
        if (focusables.length === 0) return;
        const activeIdx = focusables.indexOf(document.activeElement);
        if (e.shiftKey) {
          if (activeIdx <= 0) {
            e.preventDefault();
            focusables[focusables.length - 1].focus();
          }
        } else {
          if (activeIdx === focusables.length - 1) {
            e.preventDefault();
            focusables[0].focus();
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the modal.
    const focusTimer = window.setTimeout(() => {
      if (modalDownloadBtnRef.current) modalDownloadBtnRef.current.focus();
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
    };
    // closePreview identity is stable; safe to omit from deps
  }, [previewOpen]);

  const canReveal = status === "idle" || status === "revealed";

  const reveal = useCallback((preset) => {
    if (!canReveal) return;

    // Server-authoritative reveal path (Hub) — parent passes the result.
    // Landing path (no preset) — pick locally as before.
    let famNext;
    let propNext;
    if (preset && Number.isInteger(preset.famIdx) &&
        Number.isInteger(preset.propIdx) &&
        preset.famIdx >= 0 && preset.famIdx < FAMILY_COUNT &&
        preset.propIdx >= 0 && preset.propIdx < PROPHECIES.length) {
      famNext = preset.famIdx;
      propNext = preset.propIdx;
    } else {
      // Pick a family + prophecy that isn't identical to the previous combo.
      const prev = lastComboRef.current;
      famNext = rand(0, FAMILY_COUNT - 1);
      propNext = rand(0, PROPHECIES.length - 1);
      let guard = 0;
      while (
        guard < 10 &&
        famNext === prev.fam &&
        propNext === prev.prop
      ) {
        // rotate to a new random pair
        famNext = rand(0, FAMILY_COUNT - 1);
        propNext = rand(0, PROPHECIES.length - 1);
        guard += 1;
      }
    }
    lastComboRef.current = { fam: famNext, prop: propNext };

    if (reduceMotion) {
      // Instant reveal path — no long rotation.
      setPickedIdx(famNext);
      setProphecyIdx(propNext);
      setStatus("revealed");
      if (onRevealed) {
        try { onRevealed(famNext, propNext); } catch (_) { /* ignore */ }
      }
      return;
    }

    // Compute final wheel rotation so the picked slot lands under the top
    // pointer. Guarantees continuous, monotonically-clockwise motion:
    //  - `delta` is normalized to [0, 360) using the CURRENT accumulated
    //    rotation (not any reset value) so we never take the short path.
    //  - `fullTurns` is a random positive integer added on top so we always
    //    get several complete clockwise revolutions before decelerating.
    //  - `finalRotation` is strictly greater than `rotation` on every reveal.
    const currentMod = ((rotation % 360) + 360) % 360;
    const targetOffset = ((360 - famNext * SLOT_ANGLE) % 360 + 360) % 360;
    const delta = ((targetOffset - currentMod) % 360 + 360) % 360; // always [0, 360)
    const fullTurns = rand(MIN_SPIN_TURNS, MAX_SPIN_TURNS);
    // +1 hedge turn so we NEVER approach a target that resolves to 0
    // degrees of extra motion (which could look like a stall or reverse).
    const finalRotation = rotation + (fullTurns + 1) * 360 + delta;

    setStatus("spinning");
    setPickedIdx(null);
    setProphecyIdx(null);
    // trigger transition in next frame
    requestAnimationFrame(() => setRotation(finalRotation));

    // After the wheel finishes decelerating, play the reveal effect.
    window.setTimeout(() => {
      setPickedIdx(famNext);
      setProphecyIdx(propNext);
      setStatus("revealing");
      window.setTimeout(() => {
        setStatus("revealed");
        if (onRevealed) {
          try { onRevealed(famNext, propNext); } catch (_) { /* ignore */ }
        }
      }, REVEAL_MS);
    }, SPIN_MS);
  }, [canReveal, reduceMotion, rotation, onRevealed]);

  // Imperative handle so Hub can drive the wheel from an external CTA.
  useImperativeHandle(ref, () => ({
    triggerReveal: (preset) => reveal(preset),
    openPreview: () => openPreview(),
    status,
  }), [reveal, status]);

  const openPreview = useCallback(async () => {
    if (!pickedFamily || pickedProphecy == null) return;
    if (shareState === "generating") return;
    const cardEl = shareCardRef.current;
    if (!cardEl) return;

    setShareState("generating");
    try {
      // Wait for the card's images (family symbol + P mark) to finish
      // loading so the PNG capture doesn't miss them.
      const imgs = Array.from(cardEl.querySelectorAll("img"));
      await Promise.all(
        imgs.map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((res) => {
                const done = () => res();
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
              }),
        ),
      );
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }

      // Lazy-load html-to-image only when the user actually clicks Share.
      const { toBlob } = await import("html-to-image");
      const blob = await toBlob(cardEl, {
        width: 1080,
        height: 1350,
        pixelRatio: 1,
        cacheBust: false,
        skipFonts: true,
        backgroundColor: "#05070d",
        style: {
          position: "static",
          transform: "none",
          left: "0",
          top: "0",
          opacity: "1",
          visibility: "visible",
        },
      });
      if (!blob) throw new Error("PNG generation returned empty blob.");

      // Revoke a previous URL if the user opens the modal twice in a row.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      previewBlobRef.current = blob;
      setPreviewUrl(url);
      setPreviewFilename(`prophets-prophecy-${pickedFamily.id}.png`);
      setPreviewOpen(true);
    } catch (err) {
      console.error("[ProphecyWheel] card generation failed:", err);
    } finally {
      setShareState("idle");
    }
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setCopied(false);
  }, [pickedFamily, pickedProphecy, shareState, previewUrl]);

  const downloadPreview = useCallback(() => {
    const blob = previewBlobRef.current;
    const filename = previewFilename || "prophets-prophecy.png";
    if (!blob) return;
    // Reuse the existing generated Blob — do NOT regenerate.
    downloadBlob(blob, filename);
  }, [previewFilename]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    // Restore focus to the button that opened the modal for accessibility.
    if (shareBtnRef.current) shareBtnRef.current.focus();
  }, []);

  const isSpinning = status === "spinning";
  const isRevealed = status === "revealed" || status === "revealing";
  const showReveal = status === "revealing";
  const buttonLabel = isSpinning ? "CONSULTING THE ORACLE\u2026" : "REVEAL MY PROPHECY";

  return (
    <section
      id="oracle"
      ref={sectionRef}
      data-testid="prophecy-section"
      className="prophecy-section relative w-full px-5 md:px-10 py-20 md:py-28"
    >
      {/* Subtle continuation of Lore's orbital language — decorative only */}
      <span aria-hidden="true" className="prophecy-orbit prophecy-orbit--a" />
      <span aria-hidden="true" className="prophecy-orbit prophecy-orbit--b" />
      <span aria-hidden="true" className="prophecy-node prophecy-node--l" />
      <span aria-hidden="true" className="prophecy-node prophecy-node--r" />

      <div className="relative z-10 mx-auto max-w-[1180px] w-full flex flex-col items-center text-center">
        {/* Wheel */}
        <div className="prophecy-wheel-wrap">
          <div
            className={`prophecy-wheel ${isSpinning ? "is-spinning" : ""} ${showReveal ? "is-revealing" : ""}`}
            data-testid="prophecy-wheel"
          >
            {/* Concentric decorative rings */}
            <span aria-hidden="true" className="prophecy-ring prophecy-ring--outer" />
            <span aria-hidden="true" className="prophecy-ring prophecy-ring--mid" />
            <span aria-hidden="true" className="prophecy-ring prophecy-ring--inner" />

            {/* Fine tick marks (SVG, 60 ticks around the ring) */}
            <svg
              aria-hidden="true"
              viewBox="0 0 200 200"
              className="prophecy-ticks"
              preserveAspectRatio="xMidYMid meet"
            >
              {Array.from({ length: 60 }).map((_, i) => {
                const a = (i * 6 * Math.PI) / 180;
                const r1 = 92;
                const r2 = i % 5 === 0 ? 86 : 89;
                const x1 = 100 + Math.sin(a) * r1;
                const y1 = 100 - Math.cos(a) * r1;
                const x2 = 100 + Math.sin(a) * r2;
                const y2 = 100 - Math.cos(a) * r2;
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="currentColor"
                    strokeWidth={i % 5 === 0 ? 0.55 : 0.3}
                    opacity={i % 5 === 0 ? 0.55 : 0.28}
                  />
                );
              })}
            </svg>

            {/* Top pointer (fixed) */}
            <span aria-hidden="true" className="prophecy-pointer" />

            {/* Rotating layer with the 10 family slots */}
            <div
              className="prophecy-wheel-inner"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: reduceMotion
                  ? "none"
                  : isSpinning
                  ? `transform ${SPIN_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
            >
              {slots.map(({ fam, angle }, i) => {
                const isSelected = i === pickedIdx && (status === "revealing" || status === "revealed");
                return (
                  <div
                    key={fam.id}
                    className={`prophecy-slot ${isSelected ? "is-selected" : ""}`}
                    style={{ "--slot-angle": `${angle}deg`, "--slot-accent": fam.accent }}
                    data-testid={`prophecy-slot-${fam.id}`}
                    data-family={fam.id}
                  >
                    <div className="prophecy-slot-symbol">
                      <img
                        src={fam.image}
                        alt={fam.name}
                        width="100"
                        height="100"
                        decoding="async"
                        loading="lazy"
                        draggable={false}
                      />
                      {isSelected && showReveal && (
                        <span aria-hidden="true" className="prophecy-lockon" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Center core — standalone Prophets P medallion. Also acts
                as a secondary click target that triggers the exact same
                `reveal()` action as the main CTA button (see below). */}
            <button
              type="button"
              onClick={coreClickable ? () => reveal() : undefined}
              disabled={isSpinning || !coreClickable}
              aria-label={coreClickable ? "Reveal my prophecy" : "Prophecy wheel"}
              data-testid="prophecy-core-btn"
              className={`prophecy-core prophecy-core--btn ${isSpinning || showReveal ? "is-active" : ""}${coreClickable ? "" : " prophecy-core--display-only"}`}
              tabIndex={coreClickable ? 0 : -1}
            >
              <span className="prophecy-core-ring" aria-hidden="true" />
              <img
                src="/img/prophets_logo_mark.webp"
                alt=""
                width="220"
                height="220"
                decoding="async"
                draggable={false}
              />
              <span className="prophecy-core-sweep" aria-hidden="true" />
            </button>

            {/* Sci-fi reveal pulse (expanding ring) */}
            {showReveal && pickedFamily && (
              <span
                aria-hidden="true"
                className="prophecy-pulse"
                style={{ "--pulse-accent": pickedFamily.accent }}
              />
            )}
          </div>
        </div>

        {/* Primary activation button (landing page only). Hidden when the
            Hub renders its own CTA around this wheel. */}
        {!hideBuiltInCTA && (
          <button
            type="button"
            onClick={() => reveal()}
            disabled={isSpinning}
            data-testid="prophecy-reveal-btn"
            className="prophecy-cta"
            aria-live="polite"
          >
            <span>{buttonLabel}</span>
          </button>
        )}

        {/* Result panel — extracted to a component so it can be rendered
            in-place (desktop) or portalled to a mobile slot below the
            shared Insight frame (mobile). */}
        {!hideBuiltInResult && !isMobile && (
          <ResultCard
            isRevealed={isRevealed}
            pickedFamily={pickedFamily}
            pickedProphecy={pickedProphecy}
            reveal={() => reveal()}
            isSpinning={isSpinning}
            shareBtnRef={shareBtnRef}
            openPreview={openPreview}
            shareState={shareState}
            previewOpen={previewOpen}
          />
        )}
        {!hideBuiltInResult && isMobile && mobileSlot && createPortal(
          <ResultCard
            mobile
            isRevealed={isRevealed}
            pickedFamily={pickedFamily}
            pickedProphecy={pickedProphecy}
            reveal={() => reveal()}
            isSpinning={isSpinning}
            shareBtnRef={shareBtnRef}
            openPreview={openPreview}
            shareState={shareState}
            previewOpen={previewOpen}
          />,
          mobileSlot,
        )}
      </div>

      {/* Hidden off-screen card used only for PNG export via html-to-image */}
      {pickedFamily && pickedProphecy && (
        <ProphecyShareCard
          ref={shareCardRef}
          family={pickedFamily}
          prophecy={pickedProphecy}
        />
      )}

      {/* Preview modal — opens when the user requests a prophecy card.
          Rendered via a portal into <body> so it escapes the carousel's
          `transform` on the ProphetsInsight track (a transformed ancestor
          would otherwise become the containing block for our fixed
          positioning and drag the modal off-screen when the user is on a
          slide other than the first). The `prophets-root` wrapper keeps
          the `.prophets-root .prophecy-modal-*` scoped CSS applying. */}
      {previewOpen && typeof document !== "undefined" && createPortal(
        <div className="prophets-root">
          <div
            className="prophecy-modal-root"
            data-testid="prophecy-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="prophecy-modal-title"
          >
            <div
              className="prophecy-modal-backdrop"
              data-testid="prophecy-modal-backdrop"
              onClick={closePreview}
              aria-hidden="true"
            />
            <div
              className="prophecy-modal-panel"
              onClick={(e) => e.stopPropagation()}
              style={pickedFamily ? { "--modal-accent": pickedFamily.accent } : undefined}
            >
              <span
                id="prophecy-modal-title"
                className="p-mono prophecy-modal-label"
              >
                Your Prophecy Card
              </span>
              <button
                type="button"
                ref={modalCloseBtnRef}
                onClick={closePreview}
                className="prophecy-modal-close"
                data-testid="prophecy-modal-close"
                aria-label="Close prophecy card preview"
              >
                &times;
              </button>
              <div className="prophecy-modal-canvas" data-testid="prophecy-modal-canvas">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Your prophecy card preview"
                    className="prophecy-modal-image"
                    draggable={false}
                  />
                ) : null}
              </div>
              <div className="prophecy-modal-actions">
                <button
                  type="button"
                  ref={modalDownloadBtnRef}
                  onClick={downloadPreview}
                  className="prophecy-cta prophecy-modal-download"
                  data-testid="prophecy-modal-download"
                >
                  <span>Download PNG</span>
                </button>
                <button
                  type="button"
                  onClick={closePreview}
                  className="prophecy-secondary prophecy-modal-cancel"
                  data-testid="prophecy-modal-cancel"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
});


/**
 * ResultCard — the prophecy result panel. Rendered in-place on desktop
 * (inside the wheel section) and portalled to the mobile slot below the
 * shared Insight frame on mobile. Isolated as a component so both
 * mount points render identical DOM.
 */
function ResultCard({
  mobile = false,
  isRevealed,
  pickedFamily,
  pickedProphecy,
  reveal,
  isSpinning,
  shareBtnRef,
  openPreview,
  shareState,
  previewOpen,
}) {
  return (
    <div
      data-testid={mobile ? "prophecy-result-mobile" : "prophecy-result"}
      className={`prophecy-result ${mobile ? "prophecy-result--mobile" : ""} ${isRevealed && pickedFamily ? "is-visible" : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={pickedFamily ? { "--result-accent": pickedFamily.accent } : undefined}
    >
      {isRevealed && pickedFamily && pickedProphecy && (
        <>
          <div className="prophecy-result-symbol">
            <img
              src={pickedFamily.image}
              alt={pickedFamily.name}
              width="200"
              height="200"
              decoding="async"
            />
          </div>
          <div className="prophecy-result-copy">
            <span className="p-mono prophecy-result-label">Your Prophecy</span>
            <h3
              data-testid={mobile ? "prophecy-result-family-mobile" : "prophecy-result-family"}
              className="p-nav prophecy-result-family"
            >
              {pickedFamily.name.toUpperCase()} HAVE CHOSEN YOU
            </h3>
            <p
              data-testid={mobile ? "prophecy-result-line-mobile" : "prophecy-result-line"}
              className="prophecy-result-line"
            >
              &ldquo;{pickedProphecy}&rdquo;
            </p>
          </div>
          <div className="prophecy-result-actions">
            <button
              type="button"
              onClick={reveal}
              disabled={isSpinning}
              data-testid={mobile ? "prophecy-again-btn-mobile" : "prophecy-again-btn"}
              className="prophecy-secondary"
            >
              Reveal Again
            </button>
            <button
              type="button"
              ref={mobile ? undefined : shareBtnRef}
              onClick={openPreview}
              disabled={shareState === "generating"}
              data-testid={mobile ? "prophecy-share-btn-mobile" : "prophecy-share-btn"}
              className="prophecy-secondary prophecy-secondary--accent"
              aria-haspopup="dialog"
              aria-expanded={previewOpen}
              aria-live="polite"
            >
              {shareState === "generating" ? "Creating Prophecy Card\u2026" : "Create Prophecy Card"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
